import { FloorplanBookerResponse, FloorplanResourceAvailabilityResponse } from 'store/api-client/floorplan-types';
import { FloorplanPlacement, FloorplanPlacementBooker } from '../types';

/** Geometry values are fractions of the floorplan image, so anything outside [0, 1] is corrupt. */
const isFraction = (value: unknown): boolean =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;

/**
 * A placement is renderable only with a geometry we can position from. The BE marks geometry
 * required and the admin canvas clamps every value into [0, 1], so a missing, non-numeric or
 * out-of-range one is a broken row rather than a state to render — dropping it keeps one bad row
 * from breaking the whole map. A zero-width or zero-height rect is dropped for the same reason:
 * it would render as nothing and could never be tapped.
 */
const isRenderable = ({ geometry }: FloorplanResourceAvailabilityResponse): boolean =>
  !!geometry &&
  isFraction(geometry.x) &&
  isFraction(geometry.y) &&
  isFraction(geometry.width) &&
  isFraction(geometry.height) &&
  geometry.width > 0 &&
  geometry.height > 0;

/**
 * Wire response → the shape the native floorplan renders.
 *
 * Disabled placements are dropped. The availability endpoint returns them so the *admin* map can
 * grey them out, but an employee should not see a desk that is not bookable at all (FE plan §21.4:
 * "disabled desks simply don't appear — correct for employees").
 */
export const normalizeFloorplanPlacements = (
  resources: FloorplanResourceAvailabilityResponse[] | undefined,
): FloorplanPlacement[] =>
  (resources ?? [])
    .filter(resource => resource?.isEnabled && isRenderable(resource))
    .map(({ resourceUuid, name, type, imageUrl, geometry, isAvailable, sections, isMine, booker }) => ({
      resource: { uuid: resourceUuid, name, type, imageUrl: imageUrl ?? null },
      geometry: {
        type: geometry.type,
        x: geometry.x,
        y: geometry.y,
        width: geometry.width,
        height: geometry.height,
        // Only a turned desk carries one, so unrotated placements keep their shape.
        ...(geometry.rotation ? { rotation: geometry.rotation } : {}),
      },
      sections: [...(sections ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
      availability: { isAvailable: !!isAvailable },
      occupancy: { isMine: isMine === true, booker: normalizeBooker(booker) },
    }));

/**
 * The server strips identity per field, so a booker object can arrive with any subset of fields —
 * and an empty one (every field stripped) carries no identity at all and reads as no booker.
 */
const normalizeBooker = (booker: FloorplanBookerResponse | undefined): FloorplanPlacementBooker | null => {
  if (!booker) {
    return null;
  }

  const normalized: FloorplanPlacementBooker = {
    userUuid: booker.userUuid ?? null,
    name: booker.name ?? null,
    initials: booker.initials ?? null,
    company: booker.company ?? null,
  };

  return Object.values(normalized).some(value => value !== null) ? normalized : null;
};
